import { useMemo, useState } from "react";
import { Button } from "@a2z/ui";
import { useTranslations, useLocale } from "@/lib/i18n/intl";
import { keys } from "@/lib/i18n/portal";
import { Card, Icons, Toggle, tokens } from "./primitives";
import {
  ShippingRuleModal,
  type ShippingRule,
} from "./modals/shipping-rule-modal";
import {
  FreeShippingModal,
  type FreeShippingRuleInput,
} from "./modals/free-shipping-modal";
import { BostaIntegrationTile } from "@/modules/settings/components/integrations/BostaIntegrationTile";
import { JntIntegrationTile } from "@/modules/settings/components/integrations/JntIntegrationTile";
import { IntegrationsGrid } from "@/modules/settings/components/integrations/IntegrationsGrid";
import { useGovernoratesQuery } from "@/modules/settings/services/useGovernoratesQuery";
import { useShippingRulesQuery } from "@/modules/settings/services/useShippingRulesQuery";
import {
  useCreateShippingRuleMutation,
  useUpdateShippingRuleMutation,
  useDeleteShippingRuleMutation,
} from "@/modules/settings/services/useShippingRuleMutations";
import { useFreeShippingRulesQuery } from "@/modules/settings/services/useFreeShippingRulesQuery";
import {
  useCreateFreeShippingRuleMutation,
  useUpdateFreeShippingRuleMutation,
  useDeleteFreeShippingRuleMutation,
} from "@/modules/settings/services/useFreeShippingRuleMutations";
import type {
  GovernorateView,
  FreeShippingCriteria,
  FreeShippingCriteriaValue,
} from "@/lib/api/types";

type FreeShippingRule = FreeShippingRuleInput & {
  id: string | number;
  active: boolean;
};

const govDisplayName = (g: GovernorateView, locale: string): string =>
  (locale === "ar" ? g.nameAr : g.nameEn) ?? g.nameEn ?? g.nameAr;

const criteriaToApi = (
  c: FreeShippingRuleInput["criteria"],
): FreeShippingCriteria => {
  if (c === "date") return "DATE";
  if (c === "both") return "ORDER_AMOUNT_IN_DATE";
  return "ORDER_AMOUNT";
};

const apiCriteriaToUi = (
  c: FreeShippingCriteria,
): FreeShippingRuleInput["criteria"] => {
  if (c === "DATE") return "date";
  if (c === "ORDER_AMOUNT_IN_DATE") return "both";
  return "amount";
};

const buildCriteriaValue = (
  r: FreeShippingRuleInput,
): FreeShippingCriteriaValue => {
  if (r.criteria === "date") return { start: r.startDate, end: r.endDate };
  if (r.criteria === "both")
    return {
      start: r.startDate,
      end: r.endDate,
      value: Number(r.minAmount) || 0,
    };
  return { value: Number(r.minAmount) || 0 };
};

export function ShippingPanel({
  storeSlug,
  isMobile = false,
}: {
  storeSlug: string;
  isMobile?: boolean;
}) {
  const t = useTranslations();
  const locale = useLocale();
  const ss = keys.seller.storeSettings;
  const { data: governorates } = useGovernoratesQuery({ storeSlug });
  const { data: apiRules } = useShippingRulesQuery({ storeSlug });
  const { data: apiFreeRules } = useFreeShippingRulesQuery({ storeSlug });

  const createMut = useCreateShippingRuleMutation();
  const updateMut = useUpdateShippingRuleMutation();
  const deleteMut = useDeleteShippingRuleMutation();

  const createFreeMut = useCreateFreeShippingRuleMutation();
  const updateFreeMut = useUpdateFreeShippingRuleMutation();
  const deleteFreeMut = useDeleteFreeShippingRuleMutation();

  // Name ↔ id maps. The modal UI works in display names; the API speaks UUIDs.
  const { nameToId, idToName } = useMemo(() => {
    const n2i = new Map<string, string>();
    const i2n = new Map<string, string>();
    for (const g of governorates ?? []) {
      const name = govDisplayName(g, locale);
      n2i.set(name, g.id);
      i2n.set(g.id, name);
    }
    return { nameToId: n2i, idToName: i2n };
  }, [governorates, locale]);

  // Localized governorate display names for the modals' selectors.
  const governorateNames = useMemo(
    () => (governorates ?? []).map((g) => govDisplayName(g, locale)),
    [governorates, locale],
  );

  const rules: ShippingRule[] = useMemo(() => {
    return (apiRules ?? []).map((r) => ({
      id: r.id,
      name: r.name ?? "",
      fee: Number(r.price),
      days: r.estimatedDeliveryDays,
      zones: r.governorateIds
        .map((id) => idToName.get(id))
        .filter((n): n is string => Boolean(n)),
      active: r.isActive,
    }));
  }, [apiRules, idToName]);

  const freeRules: FreeShippingRule[] = useMemo(() => {
    return (apiFreeRules ?? []).map((r) => {
      const ui = apiCriteriaToUi(r.criteria);
      const v = r.criteriaValue;
      const minAmount =
        ui === "date" ? 0 : ((v as { value?: number }).value ?? 0);
      const startDate =
        ui === "amount" ? "" : ((v as { start?: string }).start ?? "");
      const endDate =
        ui === "amount" ? "" : ((v as { end?: string }).end ?? "");
      return {
        id: r.id,
        name: r.name ?? "",
        criteria: ui,
        minAmount,
        startDate,
        endDate,
        zones: r.governorateIds
          .map((id) => idToName.get(id))
          .filter((n): n is string => Boolean(n)),
        active: r.isActive,
      };
    });
  }, [apiFreeRules, idToName]);

  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showFreeModal, setShowFreeModal] = useState(false);
  const [editingRule, setEditingRule] = useState<ShippingRule | null>(null);

  const coveredZones = new Set(
    rules.flatMap((r) => (r.id !== editingRule?.id ? r.zones : [])),
  );

  const zonesToIds = (zones: string[]): string[] =>
    zones.map((z) => nameToId.get(z)).filter((id): id is string => Boolean(id));

  const handleSaveRule = async (
    r: Omit<ShippingRule, "id" | "active">,
  ): Promise<void> => {
    const governorateIds = zonesToIds(r.zones);
    if (editingRule) {
      await updateMut.mutateAsync({
        storeSlug,
        ruleId: String(editingRule.id),
        name: r.name,
        estimatedDeliveryDays: r.days,
        price: r.fee,
        governorateIds,
      });
    } else {
      await createMut.mutateAsync({
        storeSlug,
        name: r.name,
        estimatedDeliveryDays: r.days,
        price: r.fee,
        governorateIds,
      });
    }
    setShowRuleModal(false);
    setEditingRule(null);
  };

  const handleToggleRule = async (r: ShippingRule): Promise<void> => {
    await updateMut.mutateAsync({
      storeSlug,
      ruleId: String(r.id),
      isActive: !r.active,
    });
  };

  const handleDeleteRule = async (r: ShippingRule): Promise<void> => {
    await deleteMut.mutateAsync({ storeSlug, ruleId: String(r.id) });
  };

  const handleSaveFreeRule = async (
    r: FreeShippingRuleInput,
  ): Promise<void> => {
    await createFreeMut.mutateAsync({
      storeSlug,
      name: r.name || undefined,
      criteria: criteriaToApi(r.criteria),
      criteriaValue: buildCriteriaValue(r),
      governorateIds: zonesToIds(r.zones),
    });
    setShowFreeModal(false);
  };

  const handleToggleFreeRule = async (r: FreeShippingRule): Promise<void> => {
    await updateFreeMut.mutateAsync({
      storeSlug,
      ruleId: String(r.id),
      // Backend update accepts these fields; pass through unchanged ones too
      // to satisfy mutations that may require at least one editable field.
      criteria: criteriaToApi(r.criteria),
      criteriaValue: buildCriteriaValue(r),
      governorateIds: zonesToIds(r.zones),
    });
    // Note: backend free-shipping currently has no isActive toggle in the
    // mutation. If/when it adds one, switch to `{isActive: !r.active}` here.
    void r.active;
  };

  const handleDeleteFreeRule = async (r: FreeShippingRule): Promise<void> => {
    await deleteFreeMut.mutateAsync({ storeSlug, ruleId: String(r.id) });
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <Card
        title={t(ss.shippingZonesTitle)}
        desc={t(ss.shippingZonesDesc)}
        action={
          <Button
            variant="link"
            size="sm"
            onClick={() => {
              setEditingRule(null);
              setShowRuleModal(true);
            }}
            leadingIcon={
              <span style={{ fontSize: 14, fontWeight: 500 }}>+</span>
            }
          >
            {t(ss.addShippingRule)}
          </Button>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {rules.map((r) =>
            isMobile ? (
              <div
                key={r.id}
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 10,
                  padding: 12,
                  borderRadius: 10,
                  border: `1px solid ${tokens.border}`,
                  background: r.active ? tokens.surface : tokens.bgSoft,
                  opacity: r.active ? 1 : 0.75,
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>
                      {r.name}
                    </div>
                    <div
                      style={{
                        fontSize: 12,
                        color: tokens.text3,
                        marginTop: 2,
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                    >
                      {r.zones.slice(0, 3).join(", ")}
                      {r.zones.length > 3 ? ` +${r.zones.length - 3}` : ""}
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      flexShrink: 0,
                    }}
                  >
                    <Toggle
                      on={r.active}
                      onChange={() => void handleToggleRule(r)}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="max-lg:min-h-10 max-lg:min-w-10"
                      onClick={() => {
                        setEditingRule(r);
                        setShowRuleModal(true);
                      }}
                      aria-label={t(ss.editRule)}
                      leadingIcon={Icons.edit}
                      style={{ color: tokens.text2 }}
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      className="max-lg:min-h-10 max-lg:min-w-10"
                      onClick={() => void handleDeleteRule(r)}
                      aria-label={t(ss.deleteRule)}
                      leadingIcon={Icons.trash}
                      style={{ color: tokens.danger }}
                    />
                  </div>
                </div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    fontSize: 12,
                    color: tokens.text2,
                    flexWrap: "wrap",
                  }}
                >
                  <span
                    style={{
                      fontWeight: 600,
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    {r.fee} {t(ss.currencyEgp)}
                  </span>
                  <span
                    style={{
                      width: 3,
                      height: 3,
                      borderRadius: 999,
                      background: tokens.text3,
                    }}
                  />
                  <span>{r.days === 1 ? t(ss.sameDay) : t(ss.daysValue, { days: r.days })}</span>
                  <span
                    style={{
                      width: 3,
                      height: 3,
                      borderRadius: 999,
                      background: tokens.text3,
                    }}
                  />
                  <span
                    style={{
                      fontFamily: "var(--font-mono)",
                      color: tokens.text3,
                    }}
                  >
                    {t(ss.zonesCount, { count: r.zones.length })}
                  </span>
                </div>
              </div>
            ) : (
              <div
                key={r.id}
                style={{
                  display: "grid",
                  gridTemplateColumns: "1.2fr 1.6fr 90px 110px auto auto",
                  gap: 12,
                  alignItems: "center",
                  padding: 12,
                  borderRadius: 10,
                  border: `1px solid ${tokens.border}`,
                  background: r.active ? tokens.surface : tokens.bgSoft,
                  opacity: r.active ? 1 : 0.75,
                }}
              >
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600 }}>{r.name}</div>
                  <div
                    style={{
                      fontSize: 11,
                      color: tokens.text3,
                      marginTop: 1,
                      fontFamily: "var(--font-mono)",
                    }}
                  >
                    {r.zones.length} {t(ss.zonesUpper)}
                  </div>
                </div>
                <div
                  style={{
                    fontSize: 12,
                    color: tokens.text3,
                    whiteSpace: "nowrap",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                  }}
                >
                  {r.zones.slice(0, 3).join(", ")}
                  {r.zones.length > 3 ? ` +${r.zones.length - 3}` : ""}
                </div>
                <div
                  style={{
                    fontSize: 12,
                    fontWeight: 600,
                    fontFamily: "var(--font-mono)",
                  }}
                >
                  {r.fee} {t(ss.currencyEgp)}
                </div>
                <div style={{ fontSize: 12, color: tokens.text2 }}>
                  {r.days === 1 ? t(ss.sameDay) : t(ss.daysValue, { days: r.days })}
                </div>
                <Toggle
                  on={r.active}
                  onChange={() => void handleToggleRule(r)}
                />
                <div style={{ display: "flex", gap: 4 }}>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      setEditingRule(r);
                      setShowRuleModal(true);
                    }}
                    aria-label={t(ss.editRule)}
                    leadingIcon={Icons.edit}
                    style={{ color: tokens.text2 }}
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => void handleDeleteRule(r)}
                    aria-label={t(ss.deleteRule)}
                    leadingIcon={Icons.trash}
                    style={{ color: tokens.danger }}
                  />
                </div>
              </div>
            ),
          )}
          {rules.length === 0 ? (
            <div
              style={{
                padding: 24,
                borderRadius: 10,
                border: `1px dashed ${tokens.borderStrong}`,
                textAlign: "center",
                color: tokens.text3,
                fontSize: 13,
              }}
            >
              {t(ss.shippingEmpty)}
            </div>
          ) : null}
        </div>
      </Card>

      <Card
        title={t(ss.freeShippingTitle)}
        desc={t(ss.freeShippingDesc)}
        action={
          <Button
            variant="link"
            size="sm"
            onClick={() => setShowFreeModal(true)}
            leadingIcon={
              <span style={{ fontSize: 14, fontWeight: 500 }}>+</span>
            }
          >
            {t(ss.addFreeShipping)}
          </Button>
        }
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {freeRules.map((r) => (
            <div
              key={r.id}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: 12,
                borderRadius: 10,
                border: `1px solid ${tokens.border}`,
                background: tokens.surface,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 9,
                  background: tokens.successSoft,
                  color: tokens.successText,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 18,
                }}
              >
                🚚
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 600 }}>{r.name}</div>
                <div
                  style={{
                    fontSize: 12,
                    color: tokens.text3,
                    marginTop: 2,
                  }}
                >
                  {r.criteria === "amount"
                    ? t(ss.ordersAtLeast, { amount: r.minAmount })
                    : null}
                  {r.criteria === "date" ? t(ss.promotionPeriod) : null}
                  {r.criteria === "both"
                    ? t(ss.ordersWithinPromo, { amount: r.minAmount })
                    : null}
                  {" · "}
                  {t(ss.zonesCount, { count: r.zones.length })}
                </div>
              </div>
              <Toggle
                on={r.active}
                onChange={() => void handleToggleFreeRule(r)}
              />
              <Button
                variant="outline"
                size="sm"
                className="max-lg:min-h-10 max-lg:min-w-10"
                onClick={() => void handleDeleteFreeRule(r)}
                aria-label={t(ss.deleteFreeRule)}
                leadingIcon={Icons.trash}
                style={{ color: tokens.danger }}
              />
            </div>
          ))}
          {freeRules.length === 0 ? (
            <div
              style={{
                padding: 20,
                borderRadius: 10,
                border: `1px dashed ${tokens.borderStrong}`,
                textAlign: "center",
                color: tokens.text3,
                fontSize: 12,
              }}
            >
              {t(ss.freeShippingEmpty)}
            </div>
          ) : null}
        </div>
      </Card>

      {showRuleModal ? (
        <ShippingRuleModal
          rule={editingRule}
          governorates={governorateNames}
          coveredZones={coveredZones}
          onClose={() => {
            setShowRuleModal(false);
            setEditingRule(null);
          }}
          onSave={(r) => void handleSaveRule(r)}
        />
      ) : null}
      {showFreeModal ? (
        <FreeShippingModal
          governorates={governorateNames}
          onClose={() => setShowFreeModal(false)}
          onSave={(r) => void handleSaveFreeRule(r)}
        />
      ) : null}

      <Card title={t(ss.integrationsTitle)} desc={t(ss.integrationsDesc)}>
        <IntegrationsGrid>
          <BostaIntegrationTile storeSlug={storeSlug} />
          <JntIntegrationTile storeSlug={storeSlug} />
        </IntegrationsGrid>
      </Card>
    </div>
  );
}
